import React from 'react';
import { useTranslation } from 'react-i18next';
import { Values } from '@lib/interfaces/form';
import Typography from '@lib/components/Typography/Typography';
import Form from '@lib/components/ReactHookForm/FormContainer';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import toast from '@lib/components/Toast/Toast';
import {
  AuthenticationScopeEnum,
  useAuthenticationSessionCreateMutation,
} from 'graphql-common';
import { getLoginFormFields } from 'utils/authUtils';
import styles from '@lib/layouts/AuthLayout/AuthComponents.module.scss';
import { PATH_AUTH } from '@lib/enums/urls';
import { useLocation } from 'react-router-dom';

const { VITE_AUTH_PLATFORM_URL: AUTH_PLATFORM } = import.meta.env;

const defaultValues = {
  login: '',
  password: '',
};

function LoginForm() {
  const { t } = useTranslation();
  const { search } = useLocation();

  const [loginMutation, { loading }] = useAuthenticationSessionCreateMutation({
    onCompleted: () => {
      window.location.href = `${AUTH_PLATFORM}/${PATH_AUTH}${search}`;
    },
    onError: (error) => {
      if (
        error.networkError &&
        'statusCode' in error.networkError &&
        error.networkError.statusCode === 429
      ) {
        toast({ content: t('too-many-requests-error') });
      } else {
        toast({ content: error.message || 'Oops! Something went wrong' });
      }
    },
  });

  const fields = getLoginFormFields({ t });

  const onSubmit = (values: Values) => {
    const login = values.login as string;
    const password = values.password as string;
    loginMutation({
      variables: {
        input: { uid: login, password, scope: AuthenticationScopeEnum.User },
      },
    });
  };

  const submitBtn = () => (
    <div className={styles.loginActions}>
      <Button
        type="submit"
        buttonType={ButtonTypes.primaryFilled}
        buttonText={t('log-in')}
        leftIcon="logout"
        fullWidth
        disabled={loading}
        isLoading={loading}
      />
    </div>
  );

  return (
    <div className={styles.loginWrap}>
      <Typography variant="h2" className={styles.title}>
        {t('welcome-to-datizy')}
      </Typography>
      <Form
        disableAwayConfirmation
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        fields={fields}
        actionsComponent={submitBtn}
      />
    </div>
  );
}

export default LoginForm;
